import classNames from 'classnames'
import { format } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DEPOSITS_METRICS_CATEGORY,
  FTD_METRICS_CATEGORY,
  IB_COMMISSIONS_METRICS_CATEGORY,
  LOTS_METRICS_CATEGORY,
  NET_DEPOSITS_METRICS_CATEGORY,
  NEW_CLIENTS_METRICS_CATEGORY,
  NV_METRICS_CATEGORY,
  OPENED_TA_METRICS_CATEGORY,
} from '@pages/IntroducingBroker/Dashboard/Metrics/consts'
import {
  getTotalValueByMetricsType,
  processChartData,
} from '@pages/IntroducingBroker/Dashboard/Metrics/helpers/processChartData'
import { SectionHeader } from '@pages/IntroducingBroker/Dashboard/components/SectionHeader/SectionHeader'
import { StatCard } from '@pages/IntroducingBroker/Dashboard/components/StatCard/StatCard'
import {
  last30Days,
  last30DaysIncludingToday,
} from '@pages/IntroducingBroker/Dashboard/constants/performance'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useArabicSessionLanguage, useSessionLanguage } from '@context/SessionSettingsContext'

import { IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { useApiClient } from '@utils/ApiClient'
import { useGAEvents } from '@utils/callGAEvents'
import { ClientApiClient } from '@utils/clientApi'
import { API_DATE_FORMAT } from '@utils/date.utils'

import {
  MetricsCountDto,
  MetricsDepositsDto,
  MetricsIBCommissionsDto,
  MetricsLotsDto,
  MetricsNVDto,
  MetricsNetDepositsDto,
} from '@model/IntroducingBrokerMetricsDto'

import { dateFnsLocales } from '@global/locale/Locale'

import { useFetchOne } from '@hooks/useFetch'
import { useHorizontalControlScroll } from '@hooks/useHorizontalControlScroll'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import styles from './Metrics.module.scss'

const useAllMetricsFetch = () => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()

  const [dateRange] = useState<[Date, Date]>(last30Days())
  const [dateRangeIncludingToday] = useState<[Date, Date]>(last30DaysIncludingToday())

  const [_from, _to] = useMemo<[string, string]>(() => {
    return [format(dateRange[0], API_DATE_FORMAT), format(dateRange[1], API_DATE_FORMAT)]
  }, [dateRange])

  const [from, to] = useMemo<[string, string]>(() => {
    return [
      format(dateRangeIncludingToday[0], API_DATE_FORMAT),
      format(dateRangeIncludingToday[1], API_DATE_FORMAT),
    ]
  }, [dateRangeIncludingToday])

  const callback = useCallback(async () => {
    if (!account?.clientIntroducingBroker?.id) {
      return
    }

    const results = await Promise.allSettled([
      apiClient.getActiveClientsMetrics(account.clientIntroducingBroker.id),
      apiClient.getActiveTAMetrics(account.clientIntroducingBroker.id),
      apiClient.getDepositsMetrics(account.clientIntroducingBroker.id, from, to),
      apiClient.getFTDMetrics(account.clientIntroducingBroker.id, from, to),
      apiClient.getIBCommissionsMetrics(account.clientIntroducingBroker.id, _from, _to),
      apiClient.getLotsMetrics(account.clientIntroducingBroker.id, _from, _to),
      apiClient.getNetDepositsMetrics(account.clientIntroducingBroker.id, from, to),
      apiClient.getNewClientsMetrics(account.clientIntroducingBroker.id, _from, _to),
      apiClient.getNVMetrics(account.clientIntroducingBroker.id, _from, _to),
      apiClient.getOpenedTAMetrics(account.clientIntroducingBroker.id, from, to),
    ])

    return results.map((result) => (result.status === 'fulfilled' ? result.value : undefined)) as [
      MetricsCountDto,
      MetricsCountDto,
      MetricsDepositsDto[],
      MetricsCountDto[],
      MetricsIBCommissionsDto[],
      MetricsLotsDto[],
      MetricsNetDepositsDto[],
      MetricsCountDto[],
      MetricsNVDto[],
      MetricsCountDto[]
    ]
  }, [account, apiClient, from, to, _from, _to])

  const { data, isLoading } = useFetchOne(callback)

  const [
    activeClientsMetrics,
    activeTAMetrics,
    depositsMetrics,
    ftdMetrics,
    ibCommissionsMetrics,
    lotsMetrics,
    netDepositsMetrics,
    newClientsMetrics,
    nvMetrics,
    openedTAMetrics,
  ] = data ?? []

  return {
    activeClientsMetrics,
    activeTAMetrics,
    depositsMetrics,
    ftdMetrics,
    ibCommissionsMetrics,
    lotsMetrics,
    netDepositsMetrics,
    newClientsMetrics,
    nvMetrics,
    openedTAMetrics,
    isLoading,
  }
}

export const Metrics = () => {
  const locale = useSessionLanguage()
  const { t } = useTranslation()
  const isArabic = useArabicSessionLanguage()
  const { callGAEvents } = useGAEvents()
  const { containerRef, scroll, stopScrolling, handleScroll, showFadeLeft, showFadeRight } =
    useHorizontalControlScroll<HTMLDivElement>()

  const {
    activeClientsMetrics,
    activeTAMetrics,
    depositsMetrics,
    ftdMetrics,
    ibCommissionsMetrics,
    lotsMetrics,
    netDepositsMetrics,
    newClientsMetrics,
    nvMetrics,
    openedTAMetrics,
    isLoading,
  } = useAllMetricsFetch()

  useEffect(() => {
    requestAnimationFrame(() => {
      handleScroll()
    })
  }, [handleScroll])

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      <SectionHeader
        title={t('IB.Dashboard.Metrics.Last 30 days stats')}
        seeMoreLinkProps={{
          onClick: () => callGAEvents().IBDashboard.Metrics.seeMore(),
          to: '/dashboard/introducing-broker/reports/clients',
        }}
      />
      <Box position='relative'>
        <Box
          className={classNames(styles.iconButtonLeftContainer, styles.fadeLeft, {
            [styles.showFadeLeft]: showFadeLeft,
          })}
        >
          <Tooltip
            content={!isArabic ? t('Move Left') : t('Move Right')}
            wrapperClassName={styles.sliderControlTooltip}
          >
            <IconButton.Secondary
              icon={ArrowIcon.Left}
              onMouseDown={() => scroll(!isArabic ? 'left' : 'right')}
              onMouseUp={stopScrolling}
              onMouseLeave={stopScrolling}
              aria-label={!isArabic ? 'Scroll left' : 'Scroll right'}
            />
          </Tooltip>
        </Box>
        <Box
          className={classNames(styles.iconButtonRightContainer, styles.fadeRight, {
            [styles.showFadeRight]: showFadeRight,
          })}
        >
          <Tooltip
            content={!isArabic ? t('Move Right') : t('Move Left')}
            wrapperClassName={styles.sliderControlTooltip}
          >
            <IconButton.Secondary
              icon={ArrowIcon.Right}
              onMouseDown={() => scroll(!isArabic ? 'right' : 'left')}
              onMouseUp={stopScrolling}
              onMouseLeave={stopScrolling}
              aria-label={!isArabic ? 'Scroll right' : 'Scroll left'}
            />
          </Tooltip>
        </Box>
        <Box
          mt='x16'
          overflowX='auto'
          overflowY='hidden'
          className={styles.scrollContainer}
          ref={containerRef}
          onScroll={handleScroll}
        >
          <Box flex gap='x16'>
            <StatCard
              title={t('IB.Dashboard.Metrics.IB commissions paid')}
              value={getTotalValueByMetricsType(
                ibCommissionsMetrics,
                IB_COMMISSIONS_METRICS_CATEGORY
              )}
              chartData={{
                gradientId: IB_COMMISSIONS_METRICS_CATEGORY,
                data: processChartData(
                  ibCommissionsMetrics,
                  IB_COMMISSIONS_METRICS_CATEGORY,
                  dateFnsLocales[locale]
                ),
              }}
              unit='USD'
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.NV')}
              value={getTotalValueByMetricsType(nvMetrics, NV_METRICS_CATEGORY)}
              chartData={{
                gradientId: NV_METRICS_CATEGORY,
                data: processChartData(nvMetrics, NV_METRICS_CATEGORY, dateFnsLocales[locale]),
              }}
              unit='USD'
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.Lots')}
              value={getTotalValueByMetricsType(lotsMetrics, LOTS_METRICS_CATEGORY)}
              chartData={{
                gradientId: LOTS_METRICS_CATEGORY,
                data: processChartData(lotsMetrics, LOTS_METRICS_CATEGORY, dateFnsLocales[locale]),
              }}
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.New clients')}
              value={getTotalValueByMetricsType(newClientsMetrics, NEW_CLIENTS_METRICS_CATEGORY)}
              chartData={{
                gradientId: NEW_CLIENTS_METRICS_CATEGORY,
                data: processChartData(
                  newClientsMetrics,
                  NEW_CLIENTS_METRICS_CATEGORY,
                  dateFnsLocales[locale]
                ),
              }}
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.Deposits')}
              tooltip={t('IB.Dashboard.Metrics.Sum of transfers')}
              value={getTotalValueByMetricsType(depositsMetrics, DEPOSITS_METRICS_CATEGORY)}
              chartData={{
                gradientId: DEPOSITS_METRICS_CATEGORY,
                data: processChartData(
                  depositsMetrics,
                  DEPOSITS_METRICS_CATEGORY,
                  dateFnsLocales[locale]
                ),
              }}
              unit='USD'
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.FTDs')}
              value={getTotalValueByMetricsType(ftdMetrics, FTD_METRICS_CATEGORY)}
              chartData={{
                gradientId: FTD_METRICS_CATEGORY,
                data: processChartData(ftdMetrics, FTD_METRICS_CATEGORY, dateFnsLocales[locale]),
              }}
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.Net deposits')}
              tooltip={t('IB.Dashboard.Metrics.Sum of net transfers')}
              value={getTotalValueByMetricsType(netDepositsMetrics, NET_DEPOSITS_METRICS_CATEGORY)}
              chartData={{
                gradientId: NET_DEPOSITS_METRICS_CATEGORY,
                data: processChartData(
                  netDepositsMetrics,
                  NET_DEPOSITS_METRICS_CATEGORY,
                  dateFnsLocales[locale]
                ),
              }}
              unit='USD'
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.Active clients')}
              value={activeClientsMetrics?.count ?? 0}
              shouldRenderChart={false}
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.TAs opened')}
              value={getTotalValueByMetricsType(openedTAMetrics, OPENED_TA_METRICS_CATEGORY)}
              chartData={{
                gradientId: OPENED_TA_METRICS_CATEGORY,
                data: processChartData(
                  openedTAMetrics,
                  OPENED_TA_METRICS_CATEGORY,
                  dateFnsLocales[locale]
                ),
              }}
            />
            <StatCard
              title={t('IB.Dashboard.Metrics.Active TAs')}
              value={activeTAMetrics?.count ?? 0}
              shouldRenderChart={false}
            />
          </Box>
        </Box>
      </Box>
    </Loading>
  )
}
